import React, { useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCInput } from "react-common-components";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import Loader from "src/components/Loader";
import SidebarCard from "src/components/SidebarCard";
import { ISidebarCardItem } from "src/components/SidebarCard/interface";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import Select from "src/components/Select";
import SidebarCardButtons from "src/components/SidebarCard/sidebarCardButtons";
import AppIcons from "src/components/AppIcons";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { IPresentationFilters, IPresentationGroup, IPresentationGroupPanel } from "./interface";
import PresentationGroupContent from "./components/PresentationContent";
import { IndicatorsGroupers } from "../Panel/interface";
import ModalIndicatorValuesOfPeriod from "../Panel/components/ModalValuesOfPeriod";
import { IRefActionsModalValuesOfPeriodIndicators } from "../Panel/components/ModalValuesOfPeriod/interface";

const PresentationPanel: React.FC = () => {
	const { translate } = useTranslation();
	const history = useHistory();

	const [presentationsGroup, setPresentationsGroup] = useState<ISidebarCardItem[]>([]);	
	const indicatorsString = translate("Indicadores");
	const [defaultGrouper, setDefaultGrouper] = useState<IndicatorsGroupers>(IndicatorsGroupers.PRESENTATION_GROUP);
	const [presentationsGroupSelected, setPresentationGroupSelected] = useState<IPresentationGroup | undefined>(undefined);
	const modalValuesOfPeriodIndicators = useRef<IRefActionsModalValuesOfPeriodIndicators>(null);
	
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IPresentationFilters>({filter:"", active:"T"});
	const { handleSubmit : handleSubmitFilters, register : registerFilters, control: controlFilters, reset: resetFilters } = useForm();
	const submitFilters = (data: any) => {
		setPresentationGroupSelected(undefined);
		setFilters(data);
	};

	const { loading, errorFetching } = useEffectFetchData<IPresentationGroupPanel[]> (
		"PresentationGroups/List", 
	  { 
			...filters
		},
		 ({ data }) => {
			setPresentationsGroup(data.map((card) => ({
				id: card.iqa001Id,
				title: card.iqa001Nome,
				subtitle: `${indicatorsString}: ${card.indicatorsCount}`,
				filtered: true,
				selected: false,
				favorite: false,
				active:  card.iqa001Ativo,
				grouper: card.iqa001Agrupador
			})));
		 },
		 [filters]
	);

	if(errorFetching)
		return <RCCAlert 
			color="red"
			title={translate("Atenção")}
			description={translate("Erro ao carregar dados")}
		/>;

	if(loading)
		return <Loader />;

	return (
		<>
			<SidebarCard 
				title={translate("Apresentação de indicadores")} 
				icon={() => <AppIcons name="indicators" />}
				content={<PresentationGroupContent 
					selectedPresentationGroup={presentationsGroupSelected}
					grouperProp={defaultGrouper}
					refModalValuesOfPeriodIndicators={modalValuesOfPeriodIndicators}
				/>}
				cardClick={async (id, title, card) => {
					async function defaultGroup(){
						setDefaultGrouper(String(card.grouper) as IndicatorsGroupers);
					}
					await defaultGroup();
					setPresentationGroupSelected({
						id,
						title: title || ""
					});
				}}
				cards={presentationsGroup}
				filterContent={<FiltersContainer
					resetFilters={() => resetFilters({filter:"", active:"T"})}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					showFilters={showFilters}
				>
					<RCCGrid
						container 
						item
						xs={12}
					>
						<Select 
							control={controlFilters}
							name="active" 
							options={[
								{value: "S", label: translate("Sim")}, 
								{value: "N", label: translate("Não")},
								{value: "T", label: translate("Todos")}
							]} 
							label={translate("Ativo")}
							defaultValue={filters.active}
							submit="value"
						/>
					</RCCGrid>
					<RCCGrid
						container 
						item
						xs={12}
						styleInline="margin-bottom: 30px !important"
					>
						<RCCInput
							name="filter" 
							label={translate("Pesquisar")}
							register={registerFilters}
							defaultValue={filters.filter}
						/>
					</RCCGrid>
				</FiltersContainer>}
				buttonsContent={
					<SidebarCardButtons 
						labelButtonPlus={translate("Novo grupo")}
						onClickPlus={() => {
							const path = "/indicators/presentationgroups";
							history.push(path);
						}}
						colorFiltersButton={showFilters || filters.filter || filters.active !== "T" ? "blue" : "grey"}
						onClickFilters={() => setShowFilters((prevState) => !prevState)}
					/>
				}
			/>
			<ModalIndicatorValuesOfPeriod ref={modalValuesOfPeriodIndicators} />
		</>
	);
};

export default PresentationPanel;
